import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import { JsonLd } from '../../components/JsonLd';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';

export function TinderGuide() {
  usePageMetadata({
    title: 'Tinder Photo Guide: What Gets Right Swipes in 2026 — PhotoFeeler AI',
    description:
      'The definitive Tinder photo guide for 2026. Learn which pictures get right swipes, what to avoid, and how to test your profile photos with AI.',
    canonicalPath: '/guides/tinder',
  });

  const jsonLdData = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'Tinder Photo Guide: What Gets Right Swipes in 2026',
      description:
        'A five-step guide to building a Tinder profile that gets more right swipes, backed by research and real-world data.',
      step: [
        {
          '@type': 'HowToStep',
          name: 'Understand the 1.7-second window',
          text: 'Tinder users decide in under two seconds. Your first photo must be instantly readable with a clear face, good lighting, and no distractions.',
        },
        {
          '@type': 'HowToStep',
          name: 'Choose your lead with data, not instinct',
          text: 'Upload your top headshot options to PhotoFeeler AI and let objective scores reveal which one makes the strongest first impression.',
        },
        {
          '@type': 'HowToStep',
          name: 'Build variety into your lineup',
          text: 'Include a headshot, full-body, activity, social, and personality photo to show range and give context to who you are.',
        },
        {
          '@type': 'HowToStep',
          name: 'Eliminate the deal-breakers',
          text: 'Remove bathroom selfies, group-only shots, heavy filters, and photos older than two years. These consistently tank swipe rates.',
        },
        {
          '@type': 'HowToStep',
          name: 'Test your final lineup',
          text: 'Upload your top 3-5 photos to PhotoFeeler AI, compare the scores, and lead with the highest-rated option.',
        },
      ],
    }),
    []
  );

  return (
    <>
      <Navbar />
      <JsonLd data={jsonLdData} />
      <main className="relative overflow-hidden animate-fade-in">
        <div className="page-orb left-[-5rem] top-24 h-72 w-72 bg-rose/15" />
        <div className="page-orb right-[-6rem] top-28 h-80 w-80 bg-info/10" />

        {/* Hero */}
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-28 pb-4">
          <Link
            to="/blog"
            className="text-xs font-sans text-ink-faint hover:text-rose transition-colors mb-6 inline-block"
          >
            &larr; Blog
          </Link>
          <div className="page-hero-panel mb-12">
            <p className="section-kicker !text-left mb-3">Tinder Photo Guide</p>
            <h1
              className="font-serif text-3xl sm:text-5xl text-ink mb-5"
              style={{ fontWeight: 600, letterSpacing: '-0.03em', lineHeight: 1.1 }}
            >
              Tinder Photo Guide: What Gets Right Swipes in&nbsp;2026
            </h1>
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-sans font-medium text-rose tracking-widest uppercase">
                Dating
              </span>
              <span className="text-[10px] text-ink-faint">&middot;</span>
              <span className="text-[10px] text-ink-faint font-sans">8 min read</span>
              <span className="text-[10px] text-ink-faint">&middot;</span>
              <span className="text-[10px] text-ink-faint font-sans">March 2026</span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pb-20">
          <div className="marketing-panel p-6 sm:p-10">
            <div className="prose-custom">
              <p>
                Tinder processes over two billion swipes every day. The median decision
                time&mdash;right or left&mdash;is <strong>1.7 seconds</strong>. In that window,
                your bio is irrelevant. Your Spotify anthem is irrelevant. The only thing that
                registers is your photo. Getting your pictures right is not a nice-to-have on
                Tinder. It is the entire strategy.
              </p>
              <p>
                This guide breaks down what actually works in 2026, based on platform data,
                behavioral research, and patterns from millions of profiles. No fluff, no vague
                &ldquo;be yourself&rdquo; advice&mdash;just specific, actionable steps.
              </p>

              <h2>1. The 1.7-Second Rule: What Your First Photo Must Do</h2>
              <p>
                Your lead photo has one job: stop the thumb. In the first two seconds, the viewer&apos;s
                brain is answering three unconscious questions. Do I find this person attractive? Do
                they look trustworthy? Do they seem like they would be fun to be around? If any
                answer is unclear, the thumb keeps moving.
              </p>
              <p>
                This means your first photo must be instantly readable. One person, visible from
                roughly the chest up, looking at the camera, well lit, smiling. No sunglasses. No
                hat shadows obscuring half your face. No artsy side-profiles. No group shots where
                the viewer has to guess which one you are.
              </p>
              <p>
                Lighting is the most overlooked factor. Natural light&mdash;golden hour,
                overcast sky, or window light&mdash;flatters every skin tone and face shape.
                Overhead fluorescents and on-camera flash create harsh shadows that age you
                and flatten your features. If you take only one piece of advice from this guide,
                let it be this: shoot in natural light.
              </p>

              <h2>2. Why You Should Never Trust Your Own Judgment</h2>
              <p>
                Here is the uncomfortable truth about photo selection: you are the worst possible
                judge of your own pictures. A 2017 study published in the British Journal of
                Psychology found that people consistently choose{' '}
                <strong>less attractive photos of themselves</strong> compared to what strangers
                select. The reason is that familiarity distorts perception&mdash;you over-index on
                how a photo makes you feel rather than how it reads to someone who has never
                seen your face before.
              </p>
              <p>
                Not sure which photo to lead with?{' '}
                <Link to="/dating" className="text-rose hover:underline font-medium">
                  Upload both to PhotoFeeler
                </Link>{' '}
                and let the data decide. Three AI models score your photos on attractiveness,
                trustworthiness, and fun in about fifteen seconds&mdash;giving you an objective
                baseline that your mirror simply cannot provide.
              </p>

              <h2>3. Build a Lineup That Shows Range</h2>
              <p>
                Tinder gives you up to nine photo slots, but quality trumps quantity. Four to six
                strong photos outperform nine mediocre ones every time. The ideal lineup covers
                five distinct categories:
              </p>
              <ul>
                <li>
                  <strong>The headshot</strong> &mdash; Your lead photo, as described above.
                  Clear face, natural smile, good light.
                </li>
                <li>
                  <strong>The full-body shot</strong> &mdash; Standing in a natural setting,
                  wearing clothes that fit. This builds trust by showing you are not hiding
                  anything.
                </li>
                <li>
                  <strong>The activity photo</strong> &mdash; You doing something you genuinely
                  enjoy. Cooking, climbing, playing music, painting&mdash;anything that answers
                  the question &ldquo;What would a weekend with this person look like?&rdquo;
                </li>
                <li>
                  <strong>The social shot</strong> &mdash; A photo with one or two friends (where
                  you are clearly identifiable). This signals that other people enjoy your company.
                  Pets work too&mdash;profiles with dogs consistently see higher engagement.
                </li>
                <li>
                  <strong>The wild card</strong> &mdash; Travel, candid laughter, a dressed-up
                  event shot. Something that reveals a dimension the other photos do not cover.
                </li>
              </ul>
              <p>
                The order matters. Lead with the headshot, follow with the full-body, then
                alternate between context and personality. End with something memorable.
              </p>

              <h2>4. What to Cut Immediately</h2>
              <p>
                Certain photo types are statistically proven to hurt your swipe rate. Removing
                these is often more impactful than adding new photos:
              </p>
              <ul>
                <li>
                  <strong>Bathroom selfies</strong> &mdash; Hinge data shows these receive up to
                  90% fewer likes. The association with low effort is instant and irreversible.
                </li>
                <li>
                  <strong>All-group photos</strong> &mdash; If someone cannot identify you
                  within one second, they will swipe left rather than play detective.
                </li>
                <li>
                  <strong>Heavy filters and extreme editing</strong> &mdash; Snapchat dog ears
                  may seem like ancient history, but heavy beauty filters in 2026 signal the same
                  thing: insecurity. Authenticity outperforms polish on dating apps.
                </li>
                <li>
                  <strong>Photos older than two years</strong> &mdash; 89% of people report
                  having gone on a date where their match looked noticeably different from their
                  photos. The trust damage lasts longer than the date.
                </li>
                <li>
                  <strong>Shirtless gym mirrors</strong> &mdash; Unless you are on a beach or
                  at a pool where it is contextually appropriate, skip it. Studies consistently
                  show these reduce perceived trustworthiness.
                </li>
              </ul>

              <h2>5. Test Before You Publish</h2>
              <p>
                The difference between a good Tinder profile and a great one is not luck or
                genetics&mdash;it is iteration. The highest-performing profiles are tested and
                refined over time, not assembled once and forgotten.
              </p>
              <p>
                Before making any changes to your live profile, test your photos with
                objective feedback. Asking friends is unreliable (they are too kind). Posting on
                Reddit is chaotic (feedback varies wildly depending on who sees it). What works
                is consistent, calibrated scoring from multiple independent evaluators.
              </p>
              <p>
                Ready to test?{' '}
                <Link to="/dating" className="text-rose hover:underline font-medium">
                  Upload your top 3&ndash;5 photos to PhotoFeeler
                </Link>{' '}
                and find your strongest option in about fifteen seconds. Lead with your
                highest-scoring photo, cut your lowest, and watch your match rate climb.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            <div className="marketing-panel-warm p-8 sm:p-10">
              <h3
                className="font-serif text-2xl sm:text-3xl text-ink mb-3"
                style={{ fontWeight: 600 }}
              >
                Find your best Tinder photo
              </h3>
              <p
                className="text-sm text-ink-faint mb-6 max-w-md mx-auto"
                style={{ fontWeight: 300 }}
              >
                Three AI models rate your photos on the traits that drive right swipes.
                Results in fifteen seconds.
              </p>
              <Link to="/dating" className="btn-rose text-sm inline-block">
                Upload your dating photos &rarr;
              </Link>
            </div>
          </div>

          {/* Related reading */}
          <div className="mt-16 pt-10 border-t border-surface-border">
            <p className="section-kicker !text-left mb-6">Related Reading</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/guides/hinge" className="card lift-hover block">
                <span className="text-[10px] font-sans font-medium text-rose tracking-widest uppercase">
                  Guide
                </span>
                <h4 className="font-serif text-lg text-ink mt-2" style={{ fontWeight: 600 }}>
                  How to Choose the Best Hinge Photos (2026 Guide)
                </h4>
                <p className="text-xs text-ink-faint mt-2">8 min read</p>
              </Link>
              <Link to="/guides/bumble" className="card lift-hover block">
                <span className="text-[10px] font-sans font-medium text-rose tracking-widest uppercase">
                  Guide
                </span>
                <h4 className="font-serif text-lg text-ink mt-2" style={{ fontWeight: 600 }}>
                  Bumble Photo Strategy: Stand Out Without Trying Too Hard
                </h4>
                <p className="text-xs text-ink-faint mt-2">8 min read</p>
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
